import React from 'react';
import { useTranslate } from 'react-admin';
import { TextField, H2, AmountField, H3, GlTypeLabel } from '~/components';
import { hasTranslation } from '~/utils';
import ReferenceField from '~/components/ReferenceField';
import Grid from '@material-ui/core/Grid';
import { SenderIcon, RecipientIcon } from '~/img';
import makeStyles from '@material-ui/core/styles/makeStyles';
import cx from 'classnames';
import { tcLabel } from '../TransfersList';
import { Transfer } from '~/types/Transfer';
import { useAccountEntity } from '~/hooks/accounts';
import TransferActions from './TransferActions';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingLeft: theme.spacing(4),
  },
  topSpacing: {
    marginTop: theme.spacing(4),
  },
  bottomSpacing: {
    marginBottom: theme.spacing(2),
  },
  headerSection: {
    '& .sectionActions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& .sectionActions': {
        opacity: 1,
      },
    },
  },
}));

interface Props {
  record: Transfer;
}

const Details = (props: Props) => {
  const { record } = props;
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string, args?: any): string => translate(`resources.transfers.show.${key}`, args);
  const tc = (key: string, args?: any): string => translate(tcLabel(key), args);

  const fromAccount = useAccountEntity(record?.fromAccountId);
  const toAccount = useAccountEntity(record?.toAccountId);

  if (!record) return null;
  return (
    <>
      <Grid
        container
        justify='space-between'
        alignItems='flex-start'
        className={classes.headerSection}
      >
        <Grid item>
          <H2 topSpacing={0} bottomSpacing={3}>
            {t('transferHeader')}
          </H2>
        </Grid>

        <Grid item className='sectionActions'>
          <TransferActions record={record} />
        </Grid>
      </Grid>
      <Grid container spacing={2} className={cx(classes.container, classes.bottomSpacing)}>
        {record?.transactionPublicId && (
          <Grid item xs={4}>
            <TextField
              label={t('transactionId')}
              showLink={`/headTransactions/${record.transactionPublicId}/show`}
            >
              {`${record.transactionPublicId.substr(0, 23)}-...`}
            </TextField>
          </Grid>
        )}
        <Grid item xs={4}>
          <AmountField label={t('amount')} amount={record?.amount} />
        </Grid>
        <Grid item xs={4}>
          <TextField label={t('transferCategory')}>
            {record?.transferCategory && hasTranslation(tcLabel(record?.transferCategory))
              ? tc(record?.transferCategory)
              : undefined}
          </TextField>
        </Grid>
        <Grid item xs={8}>
          <TextField label={t('description')}>{record?.description}</TextField>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <H3 topSpacing={2} bottomSpacing={1} icon={<SenderIcon />}>
            {t('senderProfile')}
          </H3>
          <Grid container spacing={2} className={classes.container}>
            {record?.fromProfileId && record?.fromProfileType && (
              <Grid item xs={4}>
                <ReferenceField
                  record={{ id: record?.fromProfileId, type: record?.fromProfileType }}
                />
              </Grid>
            )}
            <Grid item xs={4}>
              <TextField
                label={t('senderAccount')}
                showLink={
                  record?.fromAccountId ? `/accounts/${record?.fromAccountId}/show` : undefined
                }
                copyable={
                  typeof record?.fromAccountNumber === 'string' ? record.fromAccountNumber : false
                }
              >
                <GlTypeLabel glType={fromAccount.data?.glType} text={record?.fromAccountNumber} />
              </TextField>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <H3 topSpacing={2} bottomSpacing={1} icon={<RecipientIcon />}>
            {t('recipientProfile')}
          </H3>
          <Grid container spacing={2} className={classes.container}>
            {record?.toProfileId && record?.toProfileType && (
              <Grid item xs={4}>
                <ReferenceField record={{ id: record?.toProfileId, type: record?.toProfileType }} />
              </Grid>
            )}
            <Grid item xs={4}>
              <TextField
                label={t('recipientAccount')}
                showLink={record?.toAccountId ? `/accounts/${record?.toAccountId}/show` : undefined}
                copyable={
                  typeof record?.toAccountNumber === 'string' ? record.toAccountNumber : false
                }
              >
                <GlTypeLabel glType={toAccount.data?.glType} text={record?.toAccountNumber} />
              </TextField>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default Details;
